import { Modal, TimePicker, Switch, Button, Tooltip, notification } from 'antd';
import { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getSelectedAgent, getSelectedAgentId } from '@/store/account/selector';
import { InfoCircleOutlined, SettingOutlined } from '@ant-design/icons';
import momentTimeZone from 'moment-timezone';
import { useMutation } from '@tanstack/react-query';
import { updateUserTimeZone } from '@/api/logs';
import { useAppDispatch } from '@/store/hooks/useAppDispatch';
import { setUserTimeZone } from '@/store/account/slice';
import { useLocalTimezone } from 'src/common/hooks/useLocalTimezone';
import { TWorkflowAvailabilities, TSetWorkflowAvailability } from '@/types/TWorkflowAvailabilities';

const SetupAvailability: React.FC<{
	availability?: TWorkflowAvailabilities;
	setWorkflowAvailability: TSetWorkflowAvailability;
	index: number;
	onCancel: (isModalOpen: boolean) => void;
	initialConfigEnabled?: boolean;
	initialFallbackMessage?: string;
}> = ({
	availability,
	setWorkflowAvailability,
	onCancel,
	index,
	initialConfigEnabled,
	initialFallbackMessage,
}) => {
	const format = 'HH:mm';
	const dispatch = useAppDispatch();
	const selectedAgent = useAppSelector(getSelectedAgent);
	const selectedAgentId = useAppSelector(getSelectedAgentId);
	const { timeZone } = useLocalTimezone(selectedAgent?.time_zone);
	const fallbackInputRef = useRef<HTMLInputElement>(null);

	const updateTimeZoneMutation = useMutation({
		mutationKey: ['updateTimeZone'],
		mutationFn: updateUserTimeZone,
		onSuccess(_, values) {
			dispatch(setUserTimeZone({ timeZone: values.timeZone }));
		},
	});

	const [availabilities, setAvailabilities] = useState<TWorkflowAvailabilities>(
		availability || {
			Monday: { status: true, timings: ['09:00', '17:00'] },
			Tuesday: { status: true, timings: ['09:00', '17:00'] },
			Wednesday: { status: true, timings: ['09:00', '17:00'] },
			Thursday: { status: true, timings: ['09:00', '17:00'] },
			Friday: { status: true, timings: ['09:00', '17:00'] },
			Saturday: { status: false, timings: ['09:00', '17:00'] },
			Sunday: { status: false, timings: ['09:00', '17:00'] },
		},
	);

	const [configurationAvailability, setConfigurationAvailability] = useState<boolean>(
		initialConfigEnabled ?? false,
	);

	const getTimeComponents = (time: any) => {
		return {
			hour: dayjs(time).hour().toString().padStart(2, '0'),
			minute: dayjs(time).minute().toString().padStart(2, '0'),
		};
	};

	const handleTimeChange = (day: keyof TWorkflowAvailabilities, times: any) => {
		if (!times) {
			console.log('No or invalid times');
			return;
		}
		const { hour: startHour, minute: startMinute } = getTimeComponents(times[0]);
		const { hour: endHour, minute: endMinute } = getTimeComponents(times[1]);

		setAvailabilities((prev: TWorkflowAvailabilities) => ({
			...prev,
			[day]: {
				status: prev[day].status,
				timings: [`${startHour}:${startMinute}`, `${endHour}:${endMinute}`],
			},
		}));
	};

	const handleDayStatus = (status: boolean, day: keyof TWorkflowAvailabilities) => {
		setAvailabilities((prev: TWorkflowAvailabilities) => ({
			...prev,
			[day]: { status, timings: prev[day].timings },
		}));
	};

	const handleConfigurationAvailability = (status: boolean) => {
		setConfigurationAvailability(status);
	};

	const handleSubmit = async () => {
		if (!selectedAgent?.time_zone || selectedAgent.time_zone === 'na') {
			const localTimezone = momentTimeZone.tz.guess();
			if (localTimezone !== selectedAgent?.time_zone) {
				await updateTimeZoneMutation.mutateAsync({
					timeZone: localTimezone,
					agentId: selectedAgentId as string,
				});
			}
		}

		const fallbackMessage = fallbackInputRef.current?.value?.trim();
		if (!fallbackMessage) {
			notification.error({
				message: 'Error',
				description: 'Please enter a prompt for AI during inactive hours',
			});
			return;
		}
		await setWorkflowAvailability(
			availabilities,
			index,
			fallbackMessage,
			configurationAvailability,
		);
		onCancel(false);
	};

	useEffect(() => {
		if (availability) {
			setAvailabilities(availability);
		}
		if (initialConfigEnabled !== undefined) {
			setConfigurationAvailability(initialConfigEnabled);
		}
		if (fallbackInputRef.current && initialFallbackMessage !== undefined) {
			fallbackInputRef.current.value = initialFallbackMessage;
		}
	}, [availability, initialConfigEnabled, initialFallbackMessage]);

	return (
		<div className="flex flex-col">
			<div className="mb-4 flex justify-between items-baseline">
				<p className="text-sm text-gray-600 flex items-center">
					Please set workflow availability hours for each day of the week. Timezone:
					<span className="font-semibold text-gray-500 ml-2">{timeZone}</span>
				</p>
				<div className="flex items-center gap-2">
					<span className="text-sm text-gray-500">Enable</span>
					<Switch checked={configurationAvailability} onChange={handleConfigurationAvailability} />
				</div>
			</div>

			<div
				className={`mb-2 p-4 bg-white border rounded-2xl shadow-sm ${
					!configurationAvailability
						? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed pointer-events-none'
						: 'border-indigo-200 hover:scale-[101%] hover:transition'
				}`}
			>
				{(Object.keys(availabilities) as (keyof TWorkflowAvailabilities)[]).map((day, key) => (
					<div
						key={key}
						className={`flex justify-between items-center mb-3 px-2 py-1 transition ${
							!configurationAvailability ? '' : 'hover:bg-pink-50 rounded-xl'
						}`}
					>
						<div className="flex items-center gap-3 w-1/3">
							<Switch
								disabled={!configurationAvailability}
								className={availabilities[day].status ? 'bg-black' : 'bg-rose-400'}
								data-testid="day-switch"
								checked={availabilities[day].status}
								onChange={(value) => handleDayStatus(value, day)}
							/>
							<div
								className={`text-sm font-semibold capitalize ${
									!configurationAvailability ? 'text-gray-400' : 'text-gray-800'
								}`}
							>
								{day}
							</div>
						</div>
						<div className="flex">
							{availabilities[day].status && (
								<TimePicker.RangePicker
									disabled={!configurationAvailability}
									allowClear={false}
									format={format}
									onChange={(times) => handleTimeChange(day, times)}
									value={[
										dayjs(availabilities[day].timings[0], format),
										dayjs(availabilities[day].timings[1], format),
									]}
									className={`rounded-xl border transition ${
										!configurationAvailability
											? 'border-gray-200 bg-gray-50 text-gray-400 ant-picker-disabled'
											: 'border-indigo-200 focus:border-pink-400'
									}`}
									style={{ width: '100%', maxWidth: '200px' }}
								/>
							)}
						</div>
					</div>
				))}
			</div>

			{/* Input for fallback message */}
			<div className="mb-4">
				<label
					className={`block text-sm font-medium mb-1 ${!configurationAvailability ? 'text-gray-400' : 'text-gray-700'}`}
				>
					Prompt for AI during inactive hours <span className="text-red-500">*</span>
				</label>
				<input
					ref={fallbackInputRef}
					required
					type="text"
					placeholder="e.g. We are currently closed. Please call again during working hours"
					disabled={!configurationAvailability}
					className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 placeholder:text-gray-400 ${
						!configurationAvailability
							? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
							: 'border-indigo-200 focus:ring-indigo-300 focus:border-indigo-400'
					}`}
				/>
			</div>

			<div className="flex justify-end mt-4">
				<Button
					onClick={handleSubmit}
					disabled={!configurationAvailability}
					className="transform duration-150 ease-in-out active:scale-95 px-4 py-4 rounded bg-blue-500 text-white hover:bg-blue-600"
				>
					Save
				</Button>
				<Button
					onClick={() => onCancel(false)}
					className="btn-secondary bg-blue-500 hover:bg-blue-600 text-white px-4 py-4 rounded ml-4"
				>
					Cancel
				</Button>
			</div>
		</div>
	);
};

export const SetupWorkflowAvailability: React.FC<{
	availability?: TWorkflowAvailabilities;
	setWorkflowAvailability: TSetWorkflowAvailability;
	index: number;
	isLoading: boolean;
	initialConfigEnabled?: boolean;
	initialFallbackMessage?: string;
}> = ({
	availability,
	setWorkflowAvailability,
	index,
	isLoading = false,
	initialConfigEnabled,
	initialFallbackMessage,
}) => {
	const [isModalOpen, setIsModalOpen] = useState(false);
	const infoText =
		"Set your AI workflow's operating hours. Choose business hours, weekends, or customize it to match your ideal schedule.";

	return (
		<>
			<Button
				type="primary"
				icon={<SettingOutlined />}
				onClick={() => setIsModalOpen(true)}
				loading={isLoading}
			>
				Availability
			</Button>
			<Modal
				width="50%"
				title={
					<span className="font-bold">
						Setup WorkFlow Availability
						<Tooltip title={infoText} className="mx-1">
							<InfoCircleOutlined
								style={{ fontSize: '10px' }}
								className="text-gray-400 cursor-help"
							/>
						</Tooltip>
					</span>
				}
				open={isModalOpen}
				onCancel={() => setIsModalOpen(false)}
				footer={null}
				maskClosable={false}
				destroyOnHidden={true}
			>
				<SetupAvailability
					availability={availability}
					setWorkflowAvailability={setWorkflowAvailability}
					onCancel={setIsModalOpen}
					index={index}
					initialConfigEnabled={initialConfigEnabled}
					initialFallbackMessage={initialFallbackMessage}
				/>
			</Modal>
		</>
	);
};
